import React, { useState, useMemo, useEffect } from "react";
import { Link } from "react-router-dom";
import { NEET_PHYSICS_CH2_QUESTIONS } from "../data/neetPhysicsCh2Questions";

// ─────────────────────────────────────────────────────────────────────────────
// NEET Physics — Chapter 2: Motion in a Straight Line (NCERT Class 11, Ch. 3).
// Content is drafted from the NCERT syllabus topic list; each concept has an
// explanation plus a worked example. NOT YET human-reviewed by a subject-matter
// expert — treat as a first draft and verify against NCERT before exam use.
// ─────────────────────────────────────────────────────────────────────────────

const SECTIONS = ["Kinematic Quantities", "Equations of Motion", "Graphical Analysis", "Relative Motion & Free Fall"];

const SECTION_COLORS = {
  "Kinematic Quantities":         "#2563eb",
  "Equations of Motion":          "#7c3aed",
  "Graphical Analysis":           "#dc2626",
  "Relative Motion & Free Fall":  "#16a34a",
};

const CONCEPTS = [
  // ── Kinematic Quantities ────────────────────────────────────────────────────
  { section: "Kinematic Quantities", title: "Position, distance (path length) and displacement",
    explanation: "Position locates a particle relative to a chosen origin along an axis, with a sign showing direction. Distance is the total PATH LENGTH covered — a scalar that never decreases and is always >= 0. Displacement is the change in position, x2 - x1 — a vector (in 1D, a signed number) whose magnitude can never exceed the distance travelled.",
    example: `A particle moves x=2m -> x=10m -> x=4m

Distance    = |10-2| + |4-10| = 8 + 6 = 14 m
Displacement = x_final - x_initial = 4 - 2 = 2 m

Check: distance (14 m) >= |displacement| (2 m)  -- always true` },
  { section: "Kinematic Quantities", title: "Average speed vs average velocity",
    explanation: "Average speed = total distance / total time — always positive. Average velocity = displacement / time interval — can be zero (or negative) even while the object keeps moving, since it depends only on net displacement, not the path taken.",
    example: `Object goes 100 m in 10 s, then returns 100 m in 15 s
Total time = 25 s, total distance = 200 m, net displacement = 0

Average speed    = 200 / 25 = 8 m/s
Average velocity = 0 / 25   = 0 m/s   <- moving the whole time, yet 0!` },
  { section: "Kinematic Quantities", title: "Instantaneous velocity",
    explanation: "Instantaneous velocity is the limit of the average velocity as the time interval shrinks to zero: v = lim(dt->0) dx/dt = dx/dt. Graphically, it is the slope of the TANGENT to the position-time (x-t) graph at that instant.",
    example: `x(t) = 2t^2 + 3t   (metres)
v(t) = dx/dt = 4t + 3

At t = 2 s:  v = 4(2) + 3 = 11 m/s` },
  { section: "Kinematic Quantities", title: "Instantaneous and average acceleration",
    explanation: "Instantaneous acceleration a = dv/dt = d2x/dt2, the slope of the tangent to a velocity-time (v-t) graph. Average acceleration over an interval is simply the net change in velocity divided by the time taken, Δv/Δt, and need not equal the instantaneous value at any single moment.",
    example: `v(t) = 4t + 3   ->   a = dv/dt = 4 m/s^2   (constant, since v is linear in t)

Separately: velocity changes from 5 m/s to 25 m/s in 4 s
Average acceleration = (25 - 5) / 4 = 5 m/s^2` },

  // ── Equations of Motion ─────────────────────────────────────────────────────
  { section: "Equations of Motion", title: "First equation of motion: v = u + at",
    explanation: "For uniformly (constant) accelerated motion, acceleration a = (v - u)/t, where u is initial velocity, v is velocity after time t. Rearranging gives the first equation of motion, v = u + at, which lets you find the final velocity directly.",
    example: `u = 5 m/s, a = 2 m/s^2, t = 6 s

v = u + at = 5 + 2(6) = 5 + 12 = 17 m/s` },
  { section: "Equations of Motion", title: "Second equation of motion: s = ut + (1/2)at^2",
    explanation: "Displacement under uniform acceleration equals the area under the v-t graph (a trapezium): s = average velocity x time = [(u+v)/2] x t. Substituting v = u + at and simplifying gives s = ut + (1/2)at^2.",
    example: `Same motion: u = 5 m/s, a = 2 m/s^2, t = 6 s

s = ut + (1/2)at^2 = 5(6) + 0.5(2)(36) = 30 + 36 = 66 m

Check via average velocity: (u+v)/2 x t = (5+17)/2 x 6 = 11 x 6 = 66 m  <- matches` },
  { section: "Equations of Motion", title: "Third equation of motion: v^2 = u^2 + 2as",
    explanation: "Eliminating time t between the first two equations of motion gives v^2 = u^2 + 2as. This is especially useful when time is not known or not needed, relating only initial velocity, final velocity, acceleration and displacement.",
    example: `Same motion: u = 5 m/s, a = 2 m/s^2, s = 66 m (from above)

v^2 = u^2 + 2as = 25 + 2(2)(66) = 25 + 264 = 289
v = sqrt(289) = 17 m/s   <- matches v = u + at result exactly` },
  { section: "Equations of Motion", title: "Sign convention and deceleration in 1-D motion",
    explanation: "Solving motion problems requires fixing ONE positive direction at the start; any velocity or acceleration opposite to it is negative. Deceleration (retardation) means the acceleration acts opposite to the velocity, so the object's speed decreases — it is simply a NEGATIVE acceleration in the chosen sign convention, not a separate formula.",
    example: `Car moving at u = 20 m/s brakes with deceleration a = -4 m/s^2

Time to stop:      v = u + at  ->  0 = 20 - 4t   ->  t = 5 s
Distance to stop:  v^2 = u^2 + 2as  ->  0 = 400 - 8s  ->  s = 50 m` },

  // ── Graphical Analysis ──────────────────────────────────────────────────────
  { section: "Graphical Analysis", title: "Position-time graph: slope gives velocity",
    explanation: "On an x-t graph, the slope at any point equals the instantaneous velocity there. A straight line means uniform (constant) velocity; a steeper line means greater speed; a horizontal segment means the object is at rest; a negative slope means motion in the negative direction.",
    example: `x-t graph: straight line from (0 s, 0 m) to (4 s, 20 m)

slope = (20 - 0) / (4 - 0) = 5 m/s   -- constant velocity throughout` },
  { section: "Graphical Analysis", title: "Position-time graph for uniformly accelerated motion",
    explanation: "Under constant acceleration, x(t) = x0 + ut + (1/2)at^2 traces a PARABOLA on the x-t graph (not a straight line). The slope of the tangent (instantaneous velocity) keeps changing — increasing if a and v point the same way, decreasing if they oppose.",
    example: `x(t) = 5t + t^2   (m)   [so u = 5 m/s, a = 2 m/s^2]

Instantaneous velocity at t=3 s:  v = dx/dt = 5 + 2t = 5 + 6 = 11 m/s
Average velocity 0 to 3 s:  [x(3) - x(0)] / 3 = (15+9-0)/3 = 8 m/s

-- average (8 m/s) and instantaneous-at-3s (11 m/s) differ, as expected` },
  { section: "Graphical Analysis", title: "Velocity-time graph: slope gives acceleration",
    explanation: "On a v-t graph, the slope equals the instantaneous acceleration. A straight line with positive slope means uniform acceleration; a horizontal line means zero acceleration (constant velocity); a negative slope means deceleration.",
    example: `v-t graph: straight line from (0 s, 5 m/s) to (6 s, 17 m/s)

slope = (17 - 5) / 6 = 12 / 6 = 2 m/s^2   -- matches a = 2 m/s^2 used earlier` },
  { section: "Graphical Analysis", title: "Area under a velocity-time graph gives displacement",
    explanation: "The area enclosed between a v-t graph and the time axis over an interval equals the displacement during that interval (area above the axis counts positive, below counts negative). For uniform acceleration this area is a trapezium: s = (1/2)(u+v)t — exactly the second equation of motion in disguise.",
    example: `Same v-t graph: u = 5 m/s, v = 17 m/s, t = 6 s

Area (trapezium) = (1/2)(u+v)t = (1/2)(5+17)(6) = (1/2)(22)(6) = 66 m

-- matches s = ut + (1/2)at^2 = 66 m computed earlier` },

  // ── Relative Motion & Free Fall ─────────────────────────────────────────────
  { section: "Relative Motion & Free Fall", title: "Motion under gravity (free fall) — g and sign convention",
    explanation: "Near Earth's surface, every freely falling body (ignoring air resistance) has the SAME constant downward acceleration, g ~ 9.8 m/s^2, independent of its mass. The three equations of motion apply directly, using +g for a chosen-downward-positive convention (or -g if upward is chosen positive).",
    example: `Ball dropped from rest (u = 0); take downward as positive, a = g = 9.8 m/s^2

After t = 3 s:
v = u + gt = 0 + 9.8(3) = 29.4 m/s
s = ut + (1/2)gt^2 = 0 + 0.5(9.8)(9) = 44.1 m` },
  { section: "Relative Motion & Free Fall", title: "Vertical projectile — thrown upward",
    explanation: "For a body thrown straight up, take upward as positive so a = -g. At the highest point v = 0. Time to reach max height: t_up = u/g. Maximum height: H = u^2/(2g). By symmetry, time to fall back equals t_up, so total time of flight T = 2u/g, and the body returns to the launch point with speed u (downward).",
    example: `Thrown upward with u = 19.6 m/s, g = 9.8 m/s^2

t_up = u/g = 19.6/9.8 = 2 s
H = u^2/(2g) = (19.6)^2 / (2 x 9.8) = 384.16/19.6 = 19.6 m
Total time of flight T = 2 x t_up = 4 s

Check: H = u.t_up - (1/2)g.t_up^2 = 19.6(2) - 0.5(9.8)(4) = 39.2-19.6 = 19.6 m` },
  { section: "Relative Motion & Free Fall", title: "Relative velocity in one dimension",
    explanation: "The velocity of object A relative to object B is v_AB = v_A - v_B. In 1D this is a signed subtraction along the line of motion: if A and B move the same way, v_AB is the difference of their velocities; if they move opposite ways, using signed velocities in the same subtraction automatically gives the (larger) closing/separating speed.",
    example: `Same direction: Train A at 72 km/h east = 20 m/s, Train B at 54 km/h east = 15 m/s
v_AB = 20 - 15 = 5 m/s   (A pulls away from B at 5 m/s)

Opposite directions (east positive): Train C at 54 km/h WEST -> v_C = -15 m/s
v_AC = v_A - v_C = 20 - (-15) = 35 m/s   (closing speed between A and C)` },
  { section: "Relative Motion & Free Fall", title: "Stopping distance and reaction time",
    explanation: "A driver needs a REACTION TIME t_r to perceive danger and apply brakes; during t_r the vehicle keeps moving at its original speed u, covering a reaction distance = u.t_r. Only after that do brakes produce a (roughly) uniform deceleration a, covering a braking distance = u^2/(2a). Total stopping distance = reaction distance + braking distance.",
    example: `u = 15 m/s, reaction time t_r = 0.6 s, braking deceleration a = 5 m/s^2

Reaction distance = u.t_r = 15(0.6) = 9 m
Braking distance  = u^2/(2a) = (15)^2 / (2x5) = 225/10 = 22.5 m
Total stopping distance = 9 + 22.5 = 31.5 m` },
];

const S = {
  page:  { minHeight:"100vh", background:"linear-gradient(135deg,#0f172a,#1a1a2e)", fontFamily:"'Segoe UI',sans-serif", color:"#e2e8f0", padding:"18px 16px 80px",
    userSelect:"none", WebkitUserSelect:"none", MozUserSelect:"none", msUserSelect:"none" },
  back:  { color:"#94a3b8", fontSize:13, textDecoration:"none" },
  card:  { background:"rgba(255,255,255,0.06)", border:"1px solid rgba(255,255,255,0.11)", borderRadius:14, padding:"18px", marginBottom:16 },
  chip:  (active, color) => ({
    background: active ? color : "rgba(255,255,255,0.08)",
    color: active ? "#fff" : "#94a3b8",
    border:"none", borderRadius:20, padding:"6px 14px", fontSize:12.5, fontWeight:700, cursor:"pointer", marginRight:8, marginBottom:8,
  }),
  tag: (color) => ({ background:color, color:"#fff", borderRadius:20, padding:"2px 10px", fontSize:11, fontWeight:700, display:"inline-block" }),
  pre: { background:"#0b1120", border:"1px solid rgba(255,255,255,0.08)", borderRadius:10, padding:"12px 14px", fontSize:12.5, lineHeight:1.6, color:"#a5f3b8", overflowX:"auto", fontFamily:"'Fira Code','Consolas',monospace", whiteSpace:"pre" },
  option: (bg) => ({ display:"block", width:"100%", textAlign:"left", background:bg, border:"1px solid rgba(255,255,255,0.1)", borderRadius:8, padding:"9px 12px", color:"#e2e8f0", fontSize:13, marginBottom:7, cursor:"pointer" }),
  iconBtn: (bg) => ({ background:bg, border:"none", borderRadius:8, width:30, height:30, fontSize:14, lineHeight:1, cursor:"pointer", display:"flex", alignItems:"center", justifyContent:"center", color:"#fff", flex:"none" }),
};

// ── One interactive objective question — click an option to check it ──────────
function QuizQuestion({ item, index, onAnswer }) {
  const [selected, setSelected] = useState(null);
  const answered = selected !== null;

  const pick = (i) => {
    if (answered) return;
    setSelected(i);
    onAnswer && onAnswer(i === item.correct);
  };

  return (
    <div style={S.card}>
      <span style={S.tag(SECTION_COLORS[item.section])}>{item.section}</span>
      <span style={{ fontSize:11, color:"#64748b", marginLeft:8 }}>{item.topic}</span>
      <div style={{ fontWeight:700, fontSize:14, color:"#fff", marginTop:8, marginBottom:10 }}>{index + 1}. {item.q}</div>
      {item.options.map((opt, i) => {
        let bg = "rgba(255,255,255,0.05)";
        if (answered) {
          if (i === item.correct) bg = "rgba(34,197,94,0.28)";
          else if (i === selected) bg = "rgba(239,68,68,0.28)";
        }
        return (
          <button key={i} style={S.option(bg)} onClick={() => pick(i)} disabled={answered}>
            {String.fromCharCode(65 + i)}. {opt}
          </button>
        );
      })}
      {answered && (
        <div style={{ fontSize:12.5, color:"#94a3b8", marginTop:8, lineHeight:1.6 }}>
          {selected === item.correct ? "✅ Correct — " : "❌ Not quite — "}{item.explain}
        </div>
      )}
    </div>
  );
}

export default function NeetPhysicsCh2Page() {
  const [mode, setMode] = useState("guide"); // "guide" | "practice"
  const [activeSection, setActiveSection] = useState("All");
  const [score, setScore] = useState({ correct: 0, answered: 0 });

  // ── Bookmarks — same 🔖 pattern used elsewhere on the site, persisted locally ──
  const [bookmarks, setBookmarks] = useState(() => {
    try {
      const raw = window.localStorage.getItem("neetPhysicsCh2Bookmarks");
      return raw ? JSON.parse(raw) : [];
    } catch { return []; }
  });
  const [showBookmarkedOnly, setShowBookmarkedOnly] = useState(false);

  useEffect(() => {
    try { window.localStorage.setItem("neetPhysicsCh2Bookmarks", JSON.stringify(bookmarks)); } catch { /* ignore */ }
  }, [bookmarks]);

  const toggleBookmark = (title) => {
    setBookmarks(prev => prev.includes(title) ? prev.filter(t => t !== title) : [...prev, title]);
  };

  // ── Read aloud — same speechSynthesis pattern used on Reading Notes / TNPSC ──
  const [speakingIdx, setSpeakingIdx] = useState(null);

  useEffect(() => () => { window.speechSynthesis && window.speechSynthesis.cancel(); }, []);

  const speakConcept = (c, idx) => {
    if (!window.speechSynthesis) return;
    window.speechSynthesis.cancel();
    if (speakingIdx === idx) { setSpeakingIdx(null); return; }
    const u = new window.SpeechSynthesisUtterance(`${c.title}. ${c.explanation}`);
    u.rate = 0.95;
    u.onend = () => setSpeakingIdx(null);
    u.onerror = () => setSpeakingIdx(null);
    window.speechSynthesis.speak(u);
    setSpeakingIdx(idx);
  };

  // ── Share — native share sheet, falling back to WhatsApp like the other pages ──
  const shareConcept = (c, idx) => {
    const url = `${window.location.origin}${window.location.pathname}#concept-${idx}`;
    const text = `🧪 NEET Physics — ${c.title}\n\n${c.explanation}\n\n${url}`;
    if (navigator.share) {
      navigator.share({ title: `NEET Physics — ${c.title}`, text, url }).catch(() => {});
    } else {
      window.open(`https://wa.me/?text=${encodeURIComponent(text)}`, "_blank");
    }
  };

  const filtered = useMemo(() => {
    let list = activeSection === "All" ? CONCEPTS : CONCEPTS.filter(c => c.section === activeSection);
    if (showBookmarkedOnly) list = list.filter(c => bookmarks.includes(c.title));
    return list;
  }, [activeSection, showBookmarkedOnly, bookmarks]);

  const filteredQuestions = useMemo(() => (
    activeSection === "All" ? NEET_PHYSICS_CH2_QUESTIONS : NEET_PHYSICS_CH2_QUESTIONS.filter(q => q.section === activeSection)
  ), [activeSection]);

  const handleModeChange = (m) => {
    setMode(m);
    setScore({ correct: 0, answered: 0 });
  };

  const handleAnswer = (isCorrect) => {
    setScore(s => ({ correct: s.correct + (isCorrect ? 1 : 0), answered: s.answered + 1 }));
  };

  return (
    <div
      style={S.page}
      onCopy={(e) => e.preventDefault()}
      onCut={(e) => e.preventDefault()}
      onContextMenu={(e) => e.preventDefault()}
    >
      <Link to="/neet-physics" style={S.back}>← Back to NEET Physics Menu</Link>

      <div style={{ textAlign:"center", padding:"14px 0 10px" }}>
        <div style={{ fontSize:26, fontWeight:800, color:"#fff" }}>🧪 NEET Physics — Chapter 2</div>
        <div style={{ fontSize:15, fontWeight:700, color:"#fbbf24", marginTop:4 }}>Motion in a Straight Line</div>
        <div style={{ fontSize:12.5, color:"#94a3b8", marginTop:6 }}>
          NCERT Class 11, Ch. 3 · {CONCEPTS.length} concepts explained · {NEET_PHYSICS_CH2_QUESTIONS.length} practice questions · NOT YET human-reviewed, verify against NCERT before exam use
        </div>
      </div>

      {/* Guide / Practice mode toggle */}
      <div style={{ textAlign:"center", margin:"14px 0 8px" }}>
        <button style={S.chip(mode==="guide", "#0f766e")} onClick={() => handleModeChange("guide")}>📘 Concept Guide</button>
        <button style={S.chip(mode==="practice", "#b45309")} onClick={() => handleModeChange("practice")}>🎯 Practice Questions ({NEET_PHYSICS_CH2_QUESTIONS.length})</button>
      </div>

      {/* Section filter chips */}
      <div style={{ textAlign:"center", margin:"6px 0 18px" }}>
        <button style={S.chip(activeSection==="All", "#334155")} onClick={() => setActiveSection("All")}>All</button>
        {SECTIONS.map(s => (
          <button key={s} style={S.chip(activeSection===s, SECTION_COLORS[s])} onClick={() => setActiveSection(s)}>{s}</button>
        ))}
      </div>

      {mode === "practice" ? (
        <>
          {score.answered > 0 && (
            <div style={{ textAlign:"center", fontSize:13, fontWeight:700, color:"#fbbf24", marginBottom:14 }}>
              Score: {score.correct} / {score.answered} answered correctly
            </div>
          )}
          {filteredQuestions.map((item, i) => (
            <QuizQuestion key={i} item={item} index={i} onAnswer={handleAnswer} />
          ))}
        </>
      ) : (
      <>
      {/* ── Bookmark filter ── */}
      <div style={{ textAlign:"center", margin:"0 0 14px" }}>
        <button style={S.chip(showBookmarkedOnly, "#db2777")} onClick={() => setShowBookmarkedOnly(v => !v)}>
          🔖 {showBookmarkedOnly ? "Showing Bookmarked" : "Bookmarked"} ({bookmarks.length})
        </button>
      </div>

      {/* ── Quick reference table ── */}
      <div style={S.card}>
        <div style={{ fontWeight:800, fontSize:16, color:"#fff", marginBottom:12 }}>📋 Quick Reference Table</div>
        <div style={{ overflowX:"auto" }}>
          <table style={{ width:"100%", borderCollapse:"collapse", fontSize:13 }}>
            <thead>
              <tr style={{ borderBottom:"2px solid rgba(255,255,255,0.15)" }}>
                <th style={{ textAlign:"left", padding:"6px 8px", color:"#94a3b8" }}>#</th>
                <th style={{ textAlign:"left", padding:"6px 8px", color:"#94a3b8" }}>Section</th>
                <th style={{ textAlign:"left", padding:"6px 8px", color:"#94a3b8" }}>Topic</th>
              </tr>
            </thead>
            <tbody>
              {filtered.map((c, i) => (
                <tr key={i} style={{ borderBottom:"1px solid rgba(255,255,255,0.06)" }}>
                  <td style={{ padding:"7px 8px", color:"#64748b" }}>{i + 1}</td>
                  <td style={{ padding:"7px 8px" }}><span style={S.tag(SECTION_COLORS[c.section])}>{c.section}</span></td>
                  <td style={{ padding:"7px 8px" }}>
                    <a
                      href={`#concept-${i}`}
                      style={{ color:"#38bdf8", textDecoration:"underline", fontWeight:600 }}
                      onMouseEnter={(e) => { e.currentTarget.style.color = "#7dd3fc"; }}
                      onMouseLeave={(e) => { e.currentTarget.style.color = "#38bdf8"; }}
                    >
                      {c.title}
                    </a>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      {/* ── Detailed concept cards ── */}
      {filtered.map((c, i) => {
        const isBookmarked = bookmarks.includes(c.title);
        const isSpeaking = speakingIdx === i;
        return (
        <div key={i} id={`concept-${i}`} style={S.card}>
          <div style={{ display:"flex", justifyContent:"space-between", alignItems:"flex-start", gap:8, flexWrap:"wrap" }}>
            <span style={S.tag(SECTION_COLORS[c.section])}>{c.section}</span>
            <div style={{ display:"flex", gap:6 }}>
              <button
                title={isSpeaking ? "Stop reading" : "Read aloud"}
                onClick={() => speakConcept(c, i)}
                style={S.iconBtn(isSpeaking ? "#2563eb" : "rgba(37,99,235,0.22)")}
              >{isSpeaking ? "⏹" : "🔊"}</button>
              <button
                title={isBookmarked ? "Remove bookmark" : "Bookmark"}
                onClick={() => toggleBookmark(c.title)}
                style={S.iconBtn(isBookmarked ? "#db2777" : "rgba(219,39,119,0.22)")}
              >{isBookmarked ? "🔖" : "📌"}</button>
              <button
                title="Share"
                onClick={() => shareConcept(c, i)}
                style={S.iconBtn("rgba(255,255,255,0.12)")}
              >↗</button>
            </div>
          </div>
          <div style={{ fontWeight:800, fontSize:16, color:"#fff", marginTop:8 }}>{i + 1}. {c.title}</div>
          <div style={{ fontSize:13.5, color:"#cbd5e1", lineHeight:1.7, marginTop:8 }}>{c.explanation}</div>
          <div style={{ fontSize:11, fontWeight:700, color:"#94a3b8", marginTop:12, marginBottom:6, letterSpacing:0.4 }}>EXAMPLE / WORKED CALCULATION</div>
          <pre style={S.pre}><code>{c.example}</code></pre>
        </div>
        );
      })}
      </>
      )}
    </div>
  );
}
